import React, { useEffect, useState } from "react";
import * as motion from "motion/react-m";
import {
  opacity_variant,
  stagger_variant,
} from "../../utils/animation/transitions";
import styles from "./Accordion.module.scss";
import { customSanitize } from "../../utils/customSanitize";
import useWindowSize from "../../hooks/useWindowSize";

import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { SVGFromURL } from "../SVGFromURL/SVGFromURL";
interface AccordionItemProps {
  elements: {
    title: string;
    content: string;
  }[];
  border_line: {
    color: string;
    height: number;
  };
  separator: "box" | "border";
  box_color: string;
  moduleIndex: number;
}

export const AccordionItems = ({
  elements,
  border_line,
  separator,
  moduleIndex,
}: AccordionItemProps) => {
  const [openedIndex, setOpenedIndex] = useState<number | null>(null);
  const { width } = useWindowSize();
  const { getIcon } = useGlobalContext();

  const randomId = moduleIndex;

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    const { key } = event;
    switch (key) {
      case "Enter":
      case " ":
        event.preventDefault();
        setOpenedIndex(openedIndex === index ? null : index);
        break;
      case "ArrowDown":
        event.preventDefault();
        focusNextHeader(index);
        break;
      case "ArrowUp":
        event.preventDefault();
        focusPreviousHeader(index);
        break;
      case "Home":
        event.preventDefault();
        focusHeader(0);
        break;
      case "End":
        event.preventDefault();
        focusHeader(elements.length - 1);
        break;
      case "Escape":
        event.preventDefault();
        setOpenedIndex(null);
        blurHeader();
        break;
      default:
        break;
    }
  };

  const focusNextHeader = (index: number) => {
    const nextIndex = index + 1 >= elements.length ? 0 : index + 1;
    focusHeader(nextIndex);
  };

  const focusPreviousHeader = (index: number) => {
    const prevIndex = index - 1 < 0 ? elements.length - 1 : index - 1;
    focusHeader(prevIndex);
  };

  const focusHeader = (index: number) => {
    const header = document.getElementById(`summary-${index}`);
    header?.focus();
  };

  const blurHeader = () => {
    const focusedElement = document.activeElement as HTMLElement;
    focusedElement.blur();
  };

  useEffect(() => {
    const details = document.querySelectorAll(
      `.details-${randomId}`,
    ) as NodeListOf<HTMLElement>;

    details.forEach((detail, index) => {
      const summary = detail.querySelector(
        `.summary-${randomId}`,
      ) as HTMLElement;
      const content = detail.querySelector(
        `.content-${randomId}`,
      ) as HTMLElement;

      if (summary) {
        // Calculate the height of the summary
        // the 8px is added so that we can add 2px margin to all sides of the summary
        const summaryHeight = summary.offsetHeight + 8;
        const contentHeight = content.offsetHeight;

        // Add the styles to the details tag
        if (openedIndex === index) {
          detail.style.maxHeight = `${summaryHeight + contentHeight}px`;
          // detail.style.opacity = "1";
        } else {
          detail.style.maxHeight = `${summaryHeight}px`;
          // detail.style.opacity = "0";
        }

        detail.style.overflow = "hidden";
        detail.style.transition = "all 0.3s ease-in-out";
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openedIndex, elements, width]);

  return (
    <motion.div
      variants={stagger_variant}
      initial="initial"
      whileInView="animate"
      viewport={{ once: false }}
      className={`${styles["accordion-items"]} ${styles[separator]}`}
      style={
        {
          "--border-bottom-color": `var(--${border_line.color})`,
          "--border-bottom-height": `${border_line.height}px`,
        } as React.CSSProperties
      }
    >
      {elements.map((element, index) => {
        return (
          // accordion item
          <motion.details
            key={index}
            className={`${styles["accordion-item"]} details-${randomId}`}
            open
          >
            <motion.summary
              variants={opacity_variant}
              // tabIndex={-1}
              className={`${styles["summary"]} summary-${randomId}  ${styles[separator]}`}
              onClick={(e) => {
                e.preventDefault();
                setOpenedIndex(openedIndex === index ? null : index);
              }}
              style={{
                background:
                  separator === "box"
                    ? "var(--accordion-box-color)"
                    : "transparent",
              }}
              onKeyDown={(event) => handleKeyDown(event, index)}
              id={`summary-${index}`}
              aria-expanded={openedIndex === index ? "true" : "false"}
              aria-controls={`accordion-content-${index}`}
              aria-pressed={openedIndex === index ? "true" : "false"}
            >
              <div className={`${styles["accordion-item-header"]}`}>
                <span className={`has-text-left mr-4 ${styles.title}`}>
                  {element.title}
                </span>

                <span
                  className={`${styles["icon"]} ${openedIndex === index ? styles["is-open"] : ""}`}
                >
                  <SVGFromURL width={20} height={20} src={getIcon("open")} />
                </span>
              </div>
            </motion.summary>

            <div
              className={`${styles["accordion-item-content"]} content-${randomId}  ${styles[separator]}`}
              dangerouslySetInnerHTML={{
                __html: customSanitize(element.content),
              }}
              id={`accordion-content-${index}`}
            />
          </motion.details>
        );
      })}
    </motion.div>
  );
};
